import { FiShield, FiZap, FiGlobe, FiCpu, FiFeather } from "react-icons/fi";
import { SiCodeigniter, SiPhp } from "react-icons/si";
import { LuSettings, LuUsers, LuCircleCheck, LuShieldCheck, LuPercent, LuShield, LuCpu, LuClipboardList, LuCode, LuBug, LuRocket, LuDatabase, LuClock } from "react-icons/lu";

export const codeigniterBenefits = [
  {
    icon: <FiFeather className="w-8 h-8" />,
    title: "Ultra-Lightweight",
    desc: "CodeIgniter has a very small footprint, making it incredibly fast to load and run on any server.",
  },
  {
    icon: <FiZap className="w-8 h-8" />,
    title: "Zero Configuration",
    desc: "Almost zero configuration is required to get started, ensuring rapid deployment and development.",
  },
  {
    icon: <FiShield className="w-8 h-8" />,
    title: "Clear Documentation",
    desc: "Famous for its thorough and clear documentation, making maintenance easy for any team.",
  },
];

export const codeigniterServices = [
  {
    icon: <SiCodeigniter size={36} />,
    title: "Lightweight Web App",
    description: "Building fast, powerful web applications that don't require heavy framework overhead.",
    image: "/images/cms-dev.jpg",
    link: "#",
  },
  {
    icon: <SiPhp size={36} />,
    title: "Legacy Modernization",
    description: "Modernizing older PHP apps by migrating them to the stable and fast CodeIgniter framework.",
    image: "/images/custom-dev.jpg",
    link: "#",
  },
  {
    icon: <FiCpu size={36} />,
    title: "Fast API Development",
    description: "Creating lean RESTful APIs with minimal resource consumption for high-speed performance.",
    image: "/images/api-dev.jpg",
    link: "#",
  },
];

export const codeigniterFaqs = [
  {
    q: "Why should I choose CodeIgniter development in India for my project?",
    a: "CodeIgniter development in India provides cost-effective solutions with skilled developers who can build fast, secure, and scalable web applications. The framework’s lightweight structure ensures efficient performance and easy maintenance for your projects.",
  },
  {
    q: "What are the benefits of CodeIgniter development for my business?",
    a: "CodeIgniter development allows businesses to create robust web applications with rapid development cycles. It offers excellent performance, built-in security features, easy integration with databases, and flexibility to customize applications according to your business needs.",
  },
];

export const codeigniterProcessSteps = [
  {
    step: "01",
    title: "Lean Strategy",
    description: "Identifying the core features needed and keeping the footprint as small as possible.",
    points: ["Requirement mapping", "Library selection", "Configuration setup"],
  },
  {
    step: "02",
    title: "Rapid Implementation",
    description: "Building the application logic quickly using CodeIgniter's straightforward MVC architecture.",
    points: ["MVC development", "Database orchestration", "API creation"],
  },
  {
    step: "03",
    title: "Optimization & Launch",
    description: "Fine-tuning server-side performance and deploying to production.",
    points: ["Performance tuning", "Security audit", "Deployment"],
  },
];

export const codeigniterTechStack = ["CodeIgniter", "PHP", "MySQL", "Composer", "jQuery", "Bootstrap"];

export const codeigniterHeroData = {
  backgroundImage: "/images/serviceBanners/codeigniterBanner.webp",
  whiteImage: "/images/serviceBanners/CodeigniterWhite.webp",
  subTitle: "CodeIgniter Development Services",
  title: (
    <>
      Lightweight PHP <br /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">CodeIgniter Systems</span>
    </>
  ),
  description:
    "We build fast, lightweight, and low latency PHP applications using CodeIgniter framework to deliver simple, secure, and high performance web solutions with minimal configuration and efficient backend development.",
  primaryButton: {
    title: "Get Started",
    onClick: () => console.log("Get Started clicked"),
  },
  secondaryButton: {
    title: "View Our Work",
    onClick: () => console.log("View Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "Ultra-Lightweight",
      desc: "Extremely small framework footprint for faster execution.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "MVC Pattern",
      desc: "Clean separation of logic, view, and data for structured development.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "XSS Protection",
      desc: "Built-in security with data sanitization and CSRF protection.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "No Config Bloat",
      desc: "Simple setup with minimal configuration and quick deployment.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/nodejs.webp", // Re-use generic icon or replace with codeigniter path asset
      top: "16%",
      right: "8%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(59,130,246,0.35)",
      iconGlow: "rgba(59,130,246,0.9)",
    },
  ],
};

export const codeigniterStatsData = [
  {
    icon: LuSettings,
    value: "160+",
    label: "CodeIgniter Apps",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "70+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.97%",
    label: "Service Uptime",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuCpu,
    value: "5M+",
    label: "API Requests",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "35%",
    label: "Server Overhead Saved",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Safe Queries",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

export const codeigniterSolutionsAndProcessData = {
  buildSubtitle: "WHAT WE BUILD",
  buildTitle: "CodeIgniter Solutions We Build",
  buildCards: [
    {
      title: "Lightweight Web Applications",
      desc: "Build fast and high-performance apps optimized for low-resource hosting environments.",
      icon: LuSettings,
      colorClass: "text-[#00d2ff]",
      bgClass: "bg-[#00d2ff]/10",
      borderClass: "border-[#00d2ff]/20 hover:border-[#00d2ff]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(0,210,255,0.25)]",
    },
    {
      title: "Custom MVC Backends",
      desc: "Develop structured applications with clear separation of models, views, and controllers.",
      icon: LuCpu,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "RESTful API Services",
      desc: "Create lightweight APIs with optimized performance and low server overhead.",
      icon: LuCode,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "Legacy PHP Migration",
      desc: "Convert old procedural PHP code into modern CodeIgniter architecture seamlessly.",
      icon: LuClock,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "Database Integration",
      desc: "Use secure MySQL queries with optimized Query Builder for fast data handling.",
      icon: LuDatabase,
      colorClass: "text-[#6366f1]",
      bgClass: "bg-[#6366f1]/10",
      borderClass: "border-[#6366f1]/20 hover:border-[#6366f1]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(99,102,241,0.25)]",
    },
    {
      title: "Fast Loading Portals",
      desc: "Build high-speed applications optimized for shared hosting and low bandwidth setups.",
      icon: LuRocket,
      colorClass: "text-[#d946ef]",
      bgClass: "bg-[#d946ef]/10",
      borderClass: "border-[#d946ef]/20 hover:border-[#d946ef]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(217,70,239,0.25)]",
    },
  ],
  processSubtitle: "CODEIGNITER ENGINEERING PROCESS",
  processTitle: "CodeIgniter Engineering Process",
  processSteps: [
    {
      step: "01",
      title: "Requirement Review",
      description: "Analyze features while keeping CodeIgniter lightweight and efficient.",
      icon: LuClipboardList,
    },
    {
      step: "02",
      title: "MVC Structuring",
      description: "Set up controllers, models, and views for clean architecture structure.",
      icon: LuCode,
    },
    {
      step: "03",
      title: "Database Mapping",
      description: "Configure database connections and optimized query builder setups.",
      icon: LuSettings,
    },
    {
      step: "04",
      title: "Security Layer",
      description: "Enable CSRF protection, XSS filtering, and authentication security guidelines.",
      icon: LuShieldCheck,
    },
    {
      step: "05",
      title: "Performance Tuning",
      description: "Optimize runtime queries, remove unused loaders, and improve execution speed.",
      icon: LuCircleCheck, // Build-compliant icon to circumvent compilation lookups failing
    },
    {
      step: "06",
      title: "Production Deployment",
      description: "Deploy and verify CodeIgniter applications on live hosting environments.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY CHOOSE CODEIGNITER?",
  benefitsTitle: "Why CodeIgniter is the Right Choice",
  benefits: [
    "Lightweight framework execution",
    "Minimal configuration rules required",
    "Clean MVC architecture patterns",
    "Strong and comprehensive documentation",
    "Easy deployment on shared hosting",
    "Built-in runtime security layers",
    "Fast performance output optimization",
  ],
  logoImage: "/images/serviceBanners/codeigniterBanner.webp",
  whiteLogoImage: "/images/serviceBanners/CodeigniterWhite.webp",
};

export const codeigniterPortfolioData = {
  subtitle: "OUR WORK",
  title: "CodeIgniter Solutions We're Proud Of",
  buttonText: "View All Projects",
  buttonLink: "#all-projects",
  apps: [
    {
      title: "SpeedPortal CMS",
      badge: "CodeIgniter Portal",
      badgeColor: "bg-[#00d2ff]/10 text-[#00d2ff] border-[#00d2ff]/20",
      desc: "Fast, custom content site serving 1M+ views monthly on standard shared hosting.",
      downloads: "1M+ Views/Mo",
      rating: "4.9",
      image: "/images/WebAppDevelopment/codeigner_development.png",
    },
    {
      title: "Inventory API Sync",
      badge: "Lean API System",
      badgeColor: "bg-[#a855f7]/10 text-[#a855f7] border-[#a855f7]/20",
      desc: "High-speed stock synchronizer for inventory items processing 200 reqs/sec.",
      downloads: "5M+ Daily Syncs",
      rating: "5.0",
      image: "/images/WebAppDevelopment/codeigner_development.png",
    },
  ],
};

export const codeigniterTechnologiesData = {
  techTitle: "TECHNOLOGIES WE USE",
  technologies: [
    { name: "REST API", icon: "RestAPI" },
    { name: "SQLite", icon: "SQLite" },
    { name: "Firebase", icon: "Firebase" },
    { name: "Provider", icon: "Provider" },
  ],
  trustTitle: "TRUSTED BY BUSINESSES WORLDWIDE",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google_logo" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft_logo" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon_logo" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe_logo" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm_logo" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle_logo" },
  ],
};

export const codeigniterAboutData = [
  {
    title: "CodeIgniter Development Services for Fast and Scalable Applications",
    content: (
      <>
        Modern businesses often need lightweight web applications that deliver strong performance without unnecessary complexity. Reliable{" "}
        <b className="text-text-primary font-semibold">codeigniter development services</b> help organizations build fast and scalable systems designed for efficiency and long-term
        usability. Many businesses struggle with frameworks that require excessive resources and complicated maintenance. At{" "}
        <b className="text-text-primary font-semibold">Tycho Technologies</b>, CodeIgniter solutions are developed around business objectives, application performance, and
        practical scalability to help organizations create dependable digital products that support long-term growth.
      </>
    ),
  },
  {
    title: "CodeIgniter Development Company for Efficient Web Solutions",
    content: (
      <>
        A successful web application depends on stability, performance, and ease of maintenance. A trusted{" "}
        <b className="text-text-primary font-semibold">codeigniter development company</b> helps businesses create structured applications that improve efficiency while maintaining
        smooth digital operations. Companies investing in <b className="text-text-primary font-semibold">codeigniter web development</b> often benefit from simplified architecture
        because applications remain easier to manage and scale according to business requirements. Tycho Technologies focuses on practical CodeIgniter frameworks designed to
        improve system reliability while supporting modern digital transformation goals.
      </>
    ),
  },
  {
    title: "Hire CodeIgniter Developers for Smarter Backend Systems",
    content: (
      <>
        Businesses managing evolving digital ecosystems require backend systems that remain efficient and adaptable over time. Companies looking to{" "}
        <b className="text-text-primary font-semibold">hire codeigniter developers</b> often prioritize speed, maintainability, and scalable web performance to support business
        continuity. Organizations aiming to strengthen digital infrastructures frequently combine backend flexibility with{" "}
        <a href="https://tychotechnologies.com/django-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          django web development company
        </a>{" "}
        solutions to support connected business environments and future-ready application ecosystems.
      </>
    ),
  },
  {
    title: "Why Businesses Choose a CodeIgniter Development Company in India",
    content: (
      <>
        The demand for a trusted <b className="text-text-primary font-semibold">codeigniter development company in india</b> continues to grow as businesses seek lightweight and
        scalable web application solutions. Companies investing in <b className="text-text-primary font-semibold">codeigniter development services india</b> often focus on
        operational efficiency, application flexibility, and better long-term performance. Businesses partnering with Tycho Technologies gain access to customized CodeIgniter
        frameworks designed around measurable business objectives and technical requirements. Many organizations also strengthen backend adaptability through{" "}
        <a href="https://tychotechnologies.com/laravel-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          laravel development services
        </a>{" "}
        to support scalable enterprise systems and connected digital experiences.
      </>
    ),
  },
];
